/**
 * Contexto de Autenticação
 *
 * Gerencia o estado de autenticação da aplicação VendeAi.
 *
 * Funcionalidades:
 * - Login e logout de usuários
 * - Persistência de tokens
 * - Refresh automático de tokens
 * - Dados do usuário autenticado
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { api } from '@services/api';
import { register as registerAccount } from '@services/authService';
import { apiErrorMessage } from '@utils/helpers';
import toast from 'react-hot-toast';

/**
 * Interface do usuário autenticado
 */
interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
  tenantName: string;
  avatar?: string;
  /** Status da assinatura no momento do login (EXPIRED = somente tela de assinatura) */
  subscriptionStatus?: string;
}

/**
 * Dados para login
 */
interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Opções do login
 */
interface LoginOptions {
  /** Sem toasts (a tela mostra o resultado; ex.: login automático após o cadastro) */
  silent?: boolean;
}

/**
 * Dados para registro
 */
interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone?: string;
  tenantName: string;
  tenantType: string;
  tenantDocument?: string;
  /** Plano escolhido no website (opcional) */
  planSlug?: string;
  acceptTerms: boolean;
}

/**
 * Interface do contexto de autenticação
 */
interface AuthContextData {
  /** Usuário autenticado */
  user: User | null;
  /** Se está autenticado */
  isAuthenticated: boolean;
  /** Se está carregando estado inicial */
  isLoading: boolean;
  /** Função de login */
  login: (credentials: LoginCredentials, options?: LoginOptions) => Promise<User>;
  /**
   * Função de registro. Não mostra toast de erro: a tela de cadastro explica cada
   * falha no próprio formulário (#289); no sucesso, devolve a mensagem do servidor
   */
  register: (data: RegisterData) => Promise<string>;
  /** Função de logout */
  logout: () => void;
  /** Atualiza dados do usuário */
  updateUser: (data: Partial<User>) => void;
}

/**
 * Contexto de autenticação
 */
const AuthContext = createContext<AuthContextData>({} as AuthContextData);

/**
 * Chaves do localStorage
 */
const TOKEN_KEY = 'vendeai_access_token';
const REFRESH_TOKEN_KEY = 'vendeai_refresh_token';
const USER_KEY = 'vendeai_user';

/**
 * Props do provider de autenticação
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider de Autenticação
 *
 * Envolve a aplicação e fornece funcionalidades de auth.
 *
 * @example
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // Estado do usuário
  const [user, setUser] = useState<User | null>(null);
  // Estado de carregamento inicial
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Carrega dados do usuário do localStorage na inicialização
   */
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          // Define o token no header padrão do Axios
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Tenta validar o token buscando dados do usuário
          const response = await api.get('/auth/me');

          if (response.data.success) {
            setUser(response.data.data);
            // Atualiza dados do usuário no localStorage
            localStorage.setItem(USER_KEY, JSON.stringify(response.data.data));
          } else {
            // Token inválido, limpa dados
            clearAuthData();
          }
        }
      } catch {
        // Erro ao validar token, tenta refresh
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (refreshToken) {
          try {
            await refreshAccessToken(refreshToken);
          } catch {
            // Refresh falhou, limpa dados
            clearAuthData();
          }
        } else {
          clearAuthData();
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  /**
   * Limpa dados de autenticação
   */
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  /**
   * Renova o access token usando o refresh token
   */
  const refreshAccessToken = async (refreshToken: string): Promise<void> => {
    const response = await api.post('/auth/refresh', { refreshToken });

    if (response.data.success) {
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;

      // Salva novos tokens
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

      // Atualiza header do Axios
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
  };

  /**
   * Realiza login do usuário
   */
  const login = useCallback(async (credentials: LoginCredentials, options?: LoginOptions): Promise<User> => {
    try {
      const response = await api.post('/auth/login', {
        ...credentials,
        email: credentials.email.trim().toLowerCase(),
      });

      if (response.data.success) {
        const { accessToken, refreshToken, user: userData } = response.data.data;

        // Salva tokens
        localStorage.setItem(TOKEN_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));

        // Configura header do Axios
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

        // Atualiza estado
        setUser(userData);

        if (!options?.silent) toast.success(`Bem-vindo, ${userData.name}!`);
        return userData as User;
      }
      throw new Error(response.data?.error?.message || 'Erro ao fazer login');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || apiErrorMessage(error, 'Erro ao fazer login');
      if (!options?.silent) toast.error(message);
      throw error;
    }
  }, []);

  /**
   * Realiza registro de novo usuário/estabelecimento
   */
  const register = useCallback(async (data: RegisterData): Promise<string> => {
    // Sem nova tentativa automática: um segundo envio poderia criar a conta em dobro
    // (o servidor recusa com 409 e a tela trata). Erros sobem para a tela explicar
    const result = await registerAccount(data);
    return result.message || 'Cadastro realizado com sucesso!';
  }, []);

  /**
   * Realiza logout do usuário
   */
  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (refreshToken) {
        // Notifica o backend para revogar o token
        await api.post('/auth/logout', { refreshToken });
      }
    } catch {
      // Ignora erros no logout
    } finally {
      clearAuthData();
      toast.success('Sessão encerrada com sucesso');
    }
  }, [clearAuthData]);

  /**
   * Atualiza dados do usuário
   */
  const updateUser = useCallback((data: Partial<User>) => {
    setUser(current => {
      if (!current) return null;
      const updated = { ...current, ...data };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isAuthenticated = !!user;

  const contextValue = useMemo<AuthContextData>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      register,
      logout,
      updateUser,
    }),
    [user, isAuthenticated, isLoading, login, register, logout, updateUser],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook para acessar o contexto de autenticação
 *
 * @example
 * const { user, login, logout } = useAuth();
 *
 * @returns Dados e funções do contexto de auth
 */
export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
}
